import React, { useEffect, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';

/**
 * The platform's "loading / thinking" indicator: three soft brand-coloured blobs that orbit and melt
 * into each other, with a status line and a light sweep across it — the same design as the marketplace
 * site (platformWebSite components/marketplace/thinking.tsx). It replaces the particle-cloud canvas,
 * which owner feedback called ugly and which drew off-centre (2026-10-20). Same props as before.
 */

const orbitA = keyframes`
  0%, 100% { transform: translate(-22%, -10%) scale(.62); }
  33% { transform: translate(18%, -18%) scale(.74); }
  66% { transform: translate(6%, 20%) scale(.58); }
`;
const orbitB = keyframes`
  0%, 100% { transform: translate(20%, 12%) scale(.6); }
  33% { transform: translate(-16%, 18%) scale(.7); }
  66% { transform: translate(-10%, -20%) scale(.64); }
`;
const orbitC = keyframes`
  0%, 100% { transform: translate(4%, -22%) scale(.56); }
  33% { transform: translate(-4%, 6%) scale(.66); }
  66% { transform: translate(22%, 4%) scale(.6); }
`;
const breathe = keyframes`
  0%, 100% { transform: scale(.6); opacity: .7; }
  50% { transform: scale(.72); opacity: .95; }
`;
const sweep = keyframes`
  from { background-position: 100% 0; }
  to { background-position: -150% 0; }
`;
const arrive = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 18px;
`;

const Orb = styled.div`
  position: relative;
  width: ${(p) => p.$px}px;
  height: ${(p) => p.$px}px;
  filter: blur(.4px) saturate(1.15);
`;

const blob = (bg, anim) => css`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  /* multiply melts the blobs on light backgrounds; on a dark surface (video call) it would turn them
     black, so the onDark tone uses screen instead. */
  mix-blend-mode: ${(p) => (p.$onDark ? 'screen' : 'multiply')};
  opacity: .85;
  background: ${bg};
  animation: ${anim} 2.4s cubic-bezier(.45, .05, .55, .95) infinite;
  @media (prefers-reduced-motion: reduce) {
    animation: ${breathe} 2.4s ease-in-out infinite;
  }
`;
const BlobA = styled.i`${blob('radial-gradient(circle at 35% 35%, #6f8cff, #335cf4 70%)', orbitA)}`;
const BlobB = styled.i`${blob('radial-gradient(circle at 60% 40%, #b394ff, #885de8 70%)', orbitB)}`;
const BlobC = styled.i`${blob('radial-gradient(circle at 50% 65%, #7fd6f5, #29a9dd 70%)', orbitC)}`;

// The sweep (gradient clipped to the text) must sit on the element that holds the text itself: on the
// parent, with the phrase inside an inline-block span, the browser clipped it to nothing — the phrase
// was invisible and only a thin bar of the gradient showed (owner feedback 2026-10-20).
const Text = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #7d8db0;
  span {
    display: inline-block;
    background: linear-gradient(90deg, #7d8db0 0%, #7d8db0 35%, #335cf4 50%, #7d8db0 65%, #7d8db0 100%);
    background-size: 250% 100%;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
    animation: ${arrive} .35s ease both, ${sweep} 2.2s linear infinite;
  }
  @media (prefers-reduced-motion: reduce) {
    span { animation: none; background: none; -webkit-text-fill-color: #7d8db0; color: #7d8db0; }
  }
`;

/* Small inline variants: a busy button, a search box, a list row, a chat "thinking" bubble. */
const CompactWrap = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  vertical-align: middle;
  line-height: 1;
`;
const CompactOrb = styled.span`
  position: relative;
  display: inline-block;
  flex: 0 0 auto;
  width: ${(p) => p.$px}px;
  height: ${(p) => p.$px}px;
  filter: saturate(1.15);
`;
const InlineText = styled(Text)`
  font-size: 13px;
  white-space: nowrap;
`;

const DEFAULT_PHRASES = ['Loading your space', 'Getting everything ready', 'Almost there'];

/**
 * Props:
 * - size: full variant — the old canvas box size (orb ≈ 40% of it); compact/inline — the orb in px (default 18).
 * - message: one status line; without it the full variant cycles DEFAULT_PHRASES.
 * - phrases: several status lines to cycle (e.g. a chat "thinking" bubble).
 * - compact: orb only, no text (aria-label kept) — for buttons, search boxes, list rows.
 * - inline: small orb with the status line beside it, in a row.
 * - onDark: for dark surfaces (video call), where the light-background blend would vanish.
 * - label: accessible name for the compact orb (default "Loading").
 */
/**
 * @param {{ size?: number, message?: string, phrases?: string[], compact?: boolean, inline?: boolean,
 *           onDark?: boolean, label?: string, className?: string }} props
 */
const LogoLoader = ({ size, message = '', phrases: phraseList, compact = false, inline = false, onDark = false, label = 'Loading', className }) => {
    const small = compact || inline;
    // The old canvas `size` included a lot of empty space around the cloud; the orb is ~40% of it.
    const px = small ? Math.max(10, Math.round(size || 18)) : Math.max(36, Math.round((size || 200) * 0.4));
    const phrases = message
        ? [message.replace(/\.+$|…$/, '')]
        : (Array.isArray(phraseList) && phraseList.length ? phraseList : (compact ? [] : DEFAULT_PHRASES));
    const [i, setI] = useState(0);
    useEffect(() => {
        if (phrases.length < 2) return undefined;
        const t = setInterval(() => setI((n) => (n + 1) % phrases.length), 2800);
        return () => clearInterval(t);
    }, [phrases.length]);

    const blobs = <><BlobA $onDark={onDark} /><BlobB $onDark={onDark} /><BlobC $onDark={onDark} /></>;

    if (compact) {
        return (
            <CompactWrap role="status" aria-label={message || label} className={className}>
                <CompactOrb $px={px} aria-hidden="true">{blobs}</CompactOrb>
            </CompactWrap>
        );
    }
    if (inline) {
        return (
            <CompactWrap role="status" aria-live="polite" className={className}>
                <CompactOrb $px={px} aria-hidden="true">{blobs}</CompactOrb>
                {phrases.length > 0 && <InlineText><span key={i}>{phrases[i % phrases.length]}…</span></InlineText>}
            </CompactWrap>
        );
    }
    return (
        <Wrap role="status" aria-live="polite" className={className}>
            <Orb $px={px} aria-hidden="true">{blobs}</Orb>
            <Text><span key={i}>{phrases[i % phrases.length]}…</span></Text>
        </Wrap>
    );
};

export default LogoLoader;
